import { useEffect, useState } from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import {
  ChartContainer,
  BarPlot,
  LinePlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsLegend,
  ChartsTooltip,
} from "@mui/x-charts";
import apiService from "../../../../../../service/Api";

interface UserVenueContributionItem {
  user_id: number;
  user_name: string;
  email?: string;
  venue_count?: number;
  avg_completeness_score?: number;
  total_completeness_score?: number;
}

interface UserVenueContributionResponse {
  data: UserVenueContributionItem[];
  total_users: number;
  title: string;
}

export default function CreatorInsightsWidget() {
  const [users, setUsers] = useState<string[]>([]);
  const [venueCounts, setVenueCounts] = useState<number[]>([]);
  const [avgScores, setAvgScores] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await apiService.get<UserVenueContributionResponse>(
          "/api/venues/dashboard/user-venue-contributions/"
        );

        const items = res.data?.data ?? [];
        if (!items.length) {
          setError("No user contribution data available.");
          return;
        }

        // Safely extract fields with default values
        setUsers(items.map((i) => i.user_name || "Unknown"));
        setVenueCounts(items.map((i) => i.venue_count ?? 0));
        setAvgScores(items.map((i) => i.avg_completeness_score ?? 0));
      } catch (err) {
        console.error("Failed to fetch user venue contributions:", err);
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>{error}</Typography>;
  if (!users.length) return <Typography>No data available</Typography>;

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Creator Insights (Volume vs Quality)
        </Typography>

        <Box sx={{ height: 380 }}>
          <ChartContainer
            xAxis={[
              {
                id: "creators",
                scaleType: "band",
                data: users,
              },
            ]}
            yAxis={[
              {
                id: "venues",
                label: "Number of Venues",
                position: "left",
              },
              {
                id: "quality",
                label: "Avg Completeness %",
                position: "right",
                min: 0,
                max: 100,
              },
            ]}
            series={[
              {
                type: "bar",
                yAxisId: "venues",
                data: venueCounts,
                label: "Venues Contributed",
              },
              {
                type: "line",
                yAxisId: "quality",
                data: avgScores,
                label: "Avg Quality %",
                curve: "linear",
                color: "#d32f2f",
              },
            ]}
          >
            <BarPlot />
            <LinePlot />
            <ChartsXAxis />
            <ChartsYAxis axisId="venues" />
            <ChartsYAxis axisId="quality" />
            <ChartsLegend />
            <ChartsTooltip />
          </ChartContainer>
        </Box>
      </CardContent>
    </Card>
  );
}
